import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import CaseIcon from '@components/icons/Case.svg';
import EmailIcon from '@components/icons/Email.svg';
import PhoneIcon from '@components/icons/Phone.svg';
import UserIcon from '@components/icons/User.svg';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { accountTypes } from 'app/types/accountTypes';
import { normalizeEmail } from '@helpers/normalizeEmail';
import { useAPI } from '@providers/useAPI';
import { StyledSelect } from '@pages/Anonymous/RegistrationPage/components/Content/styles/StyledSelect';
import { AdminService } from 'app/API';
import React, { useState } from 'react';

import { ButtonWrapper } from './styles/ButtonWrapper';
import { InputWrapper } from './styles/InputWrapper';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

export const CreateUserModal = ({ onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [accountType, setAccountType] = useState('');
  const [loading, setLoading] = useState(false);

  const isValid = email && firstName && secondName;

  const accountTypeOptions = Object.entries(accountTypes).map(([key, value]) => {
    return { value: key, label: value };
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!isValid) {
      return;
    }
    setLoading(true);
    let requestErrorMessage: string | undefined;
    try {
      const response = await call(
        () =>
          AdminService.createUser({
            requestBody: {
              email: normalizeEmail(email),
              name: firstName,
              lastName: secondName,
              accountType,
              phone,
              company,
            },
          }),
        {
          onError: (message) => {
            requestErrorMessage = message;
          },
        },
      );
      if (response?.success === false) {
        setErrorMessage(String(response?.message || 'Something went wrong'));
        return;
      }
      onSuccess();
    } catch {
      setErrorMessage(requestErrorMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper width="330px">
      {loading && <AbsoluteSpinner overlay={true} />}
      <ModalTitle>Create New User</ModalTitle>
      <GenericPageError message={errorMessage} />
      <form onSubmit={handleSubmit}>
        <InputWrapper>
          <Input
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
            name="firstName"
            label="First name"
            type="text"
            value={firstName}
            required={true}
            icon={UserIcon}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="lastName"
            label="Last name"
            type="text"
            value={secondName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSecondName(event.target.value)}
            required={true}
            icon={UserIcon}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="email"
            label="Email"
            type="email"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
            required={true}
            icon={EmailIcon}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="phone"
            label="Phone"
            type="phone"
            value={phone}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPhone(event.target.value)}
            icon={PhoneIcon}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="company"
            label="Company"
            value={company}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCompany(event.target.value)}
            icon={CaseIcon}
          />
        </InputWrapper>

        <InputWrapper>
          <StyledSelect
            name="accountType"
            label="Account Type"
            value={accountType ?? ''}
            onChange={(_name, value) => {
              setAccountType(value);
            }}
            options={accountTypeOptions}
            required={true}
            hideSelectedOption={true}
          />
        </InputWrapper>

        <ButtonWrapper>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button type="submit" disabled={!isValid}>
            Create User
          </Button>
        </ButtonWrapper>
      </form>
    </ModalWrapper>
  );
};
